import type { Faro, TransportItem } from '@grafana/faro-web-sdk'
import { getWebInstrumentations, initializeFaro, TransportItemType } from '@grafana/faro-web-sdk'

import type { FaroConfig } from './config'
import { isLikelyCrawler } from './crawler'
import { stripQueryAndHash } from './url'

function matchesIgnore(message: string, patterns: (RegExp | string)[]): boolean {
  for (const p of patterns) {
    if (typeof p === 'string') {
      if (message.includes(p)) {
        return true
      }
    } else if (p.test(message)) {
      return true
    }
  }
  return false
}

/**
 * Faro's `beforeSend`: strips the query and fragment from the page URL every
 * item carries (see ./url.ts for why), then drops ignored exceptions.
 */
export function buildBeforeSend(ignore: (RegExp | string)[]): (item: TransportItem) => TransportItem | null {
  return (item) => {
    const page = item.meta?.page
    if (page?.url) {
      // Copied, not mutated: the meta object may be shared across items.
      item = { ...item, meta: { ...item.meta, page: { ...page, url: stripQueryAndHash(page.url) } } }
    }
    if (ignore.length > 0 && item.type === TransportItemType.EXCEPTION) {
      const payload = item.payload as { value?: string; type?: string }
      const message = payload?.value ?? ''
      if (matchesIgnore(message, ignore)) {
        return null
      }
    }
    return item
  }
}

export function initFaro(config: FaroConfig): Faro | undefined {
  if (typeof globalThis.window === 'undefined') {
    return undefined
  }

  // Crawlers report exceptions no user experiences and no engineer can fix —
  // Googlebot's unsupported `serviceWorker.register()` rejection alone was 31
  // of ordo's ~280 client exceptions in 24h. Bail before `initializeFaro` so a
  // bot ships no telemetry at all, not merely filtered exceptions. `pushError`
  // already no-ops when Faro was never initialized. See ./crawler.ts.
  if (isLikelyCrawler(globalThis.navigator?.userAgent)) {
    return undefined
  }

  // Same rule, different tell: a scraper that spoofs a plain Chrome user agent
  // gets past the token list, but WebDriver-driven Chromium still sets
  // `navigator.webdriver`. This is what a Playwright scraper hitting
  // www.ordo.com was — it injected a script that reads every iframe's document
  // and console.errored on each cross-origin block, permanently. Our own
  // Playwright runs are automation too and have nothing to report either.
  if (globalThis.navigator?.webdriver === true) {
    return undefined
  }

  return initializeFaro({
    url: config.collectorUrl,
    app: {
      name: config.appName,
      environment: config.environment ?? 'development',
    },
    instrumentations: [...getWebInstrumentations()],
    beforeSend: buildBeforeSend(config.ignoreErrors ?? []),
  })
}
